import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { errorMessageKey } from '@/shared/lib/errors';
import { normalizeSaudiPhone } from '@/shared/lib/phone';
import { Async } from '@/shared/ui/Async';
import { Button } from '@/shared/ui/Button';
import { TextField } from '@/shared/ui/TextField';

import { useAddMasjidMember, useMasjidMembers, useRemoveMasjidMember } from '../hooks/use-masjids';

/** Owner-only: add/remove co-managers of a masjid (Phase 4, D-010). */
export function MasjidMembersRoute(): JSX.Element {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const members = useMasjidMembers(id);
  const add = useAddMasjidMember(id);
  const remove = useRemoveMasjidMember(id);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  function submitAdd(): void {
    setError(null);
    const normalized = normalizeSaudiPhone(phone);
    if (!normalized) {
      setError(t('auth.phoneInvalid'));
      return;
    }
    add.mutate(normalized, {
      onSuccess: () => setPhone(''),
      onError: (e) => setError(t(errorMessageKey(e))),
    });
  }

  return (
    <main className="page stack">
      <h1>{t('members.title')}</h1>
      <div className="card stack">
        <TextField
          label={t('members.addByPhone')}
          placeholder={t('auth.phoneHint')}
          value={phone}
          error={error ?? undefined}
          ltr
          inputMode="tel"
          onChange={(e) => setPhone(e.target.value)}
        />
        <Button loading={add.isPending} onClick={submitAdd}>
          {t('members.add')}
        </Button>
      </div>

      <Async query={members} isEmpty={(l) => l.length === 0} emptyMessage={t('members.empty')}>
        {(list) => (
          <div className="stack">
            {list.map((m) => (
              <div key={m.id} className="card row row--between">
                <strong>{m.memberName ?? t('application.providerFallback')}</strong>
                <Button
                  variant="danger"
                  loading={remove.isPending}
                  onClick={() => remove.mutate(m.id)}
                >
                  {t('members.remove')}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Async>
    </main>
  );
}
